import React, { useEffect, useRef, useMemo, useState } from "react";
import { Card, Select } from "antd";
import Chart from "chart.js/auto";

const { Option } = Select;

const OrdersChart = ({ data }) => {
  const chartRef = useRef(null);
  const [timeFrame, setTimeFrame] = useState("daily");

  // Process the data from the backend or use default sample data
  const chartData = useMemo(() => {
    // Default sample data for different time frames
    const defaultData = {
      daily: {
        labels: Array.from({ length: 31 }, (_, i) => i + 1),
        values: Array.from({ length: 31 }, () => Math.floor(Math.random() * 100) + 10)
      },
      weekly: {
        labels: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"],
        values: Array.from({ length: 5 }, () => Math.floor(Math.random() * 500) + 100)
      },
      monthly: {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        values: Array.from({ length: 12 }, () => Math.floor(Math.random() * 1000) + 200)
      },
      yearly: {
        labels: ["2018", "2019", "2020", "2021", "2022", "2023"],
        values: Array.from({ length: 6 }, () => Math.floor(Math.random() * 10000) + 2000)
      }
    };

    // If no data is provided, return the default data
    if (!data) {
      return defaultData;
    }

    try {
      // CASE 1: Data is already structured with daily, weekly, monthly, yearly properties
      if (data.daily && data.weekly && data.monthly && data.yearly) {
        
        // Process daily data
        const processedDaily = {
          labels: [],
          values: []
        };
        
        if (Array.isArray(data.daily) && data.daily.length > 0) {
          processedDaily.labels = data.daily.map(item => {
            // Format the date to be more readable (e.g., 'Oct 14')
            if (item.date) {
              try {
                const dateObj = new Date(item.date);
                return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } catch (e) {
                return item.date;
              }
            }
            return '';
          });
          processedDaily.values = data.daily.map(item => item.orders || 0);
        }
        
        // Process weekly data
        const processedWeekly = {
          labels: [],
          values: []
        };
        
        if (Array.isArray(data.weekly) && data.weekly.length > 0) {
          processedWeekly.labels = data.weekly.map(item => item.week || '');
          processedWeekly.values = data.weekly.map(item => item.orders || 0);
        }
        
        // Process monthly data
        const processedMonthly = {
          labels: [],
          values: []
        };
        
        // Create a map of month indexes to month names
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        
        if (Array.isArray(data.monthly) && data.monthly.length > 0) {
          // Sort monthly data by month
          const sortedMonthly = [...data.monthly].sort((a, b) => {
            if (!a.month || !b.month) return 0;
            return a.month.localeCompare(b.month);
          });
          
          processedMonthly.labels = sortedMonthly.map(item => {
            if (item.month) {
              try {
                // Extract month from 'YYYY-MM' format
                const monthIndex = parseInt(item.month.split('-')[1], 10) - 1; // 0-11
                return monthNames[monthIndex] || item.month;
              } catch (e) {
                return item.month;
              }
            }
            return '';
          });
          processedMonthly.values = sortedMonthly.map(item => item.orders || 0);
        } else {
          processedMonthly.labels = monthNames;
          processedMonthly.values = Array(12).fill(0);
        }
        
        // Process yearly data
        const processedYearly = {
          labels: [],
          values: []
        };
        
        if (Array.isArray(data.yearly) && data.yearly.length > 0) {
          // Sort yearly data by year
          const sortedYearly = [...data.yearly].sort((a, b) => {
            if (!a.year || !b.year) return 0;
            return a.year.localeCompare(b.year);
          });
          
          processedYearly.labels = sortedYearly.map(item => item.year || '');
          processedYearly.values = sortedYearly.map(item => item.orders || 0);
        }
        
        return {
          daily: processedDaily,
          weekly: processedWeekly,
          monthly: processedMonthly,
          yearly: processedYearly
        };
      }
      
      // CASE 2: Data is an array (legacy format - just daily data)
      if (Array.isArray(data)) {
        
        // Format for dailyOrders: array of objects with date and orders properties
        const processedData = {
          daily: {
            labels: data.map(item => {
              // Format the date to be more readable (e.g., 'Oct 14')
              if (item.date) {
                try {
                  const dateObj = new Date(item.date);
                  return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                } catch (e) {
                  return item.date;
                }
              }
              return '';
            }),
            values: data.map(item => item.orders || 0)
          },
          // Use default data for weekly, monthly and yearly
          weekly: defaultData.weekly,
          monthly: defaultData.monthly,
          yearly: defaultData.yearly
        };
        
        return processedData;
      }

      // If we can't process the data, return the default data
      return defaultData;
    } catch (error) {
      console.error('Error processing orders chart data:', error);
      return defaultData;
    }
  }, [data]);

  // Get the current data based on selected time frame
  const currentData = useMemo(() => {
    if (!chartData || !chartData[timeFrame]) {
      return { labels: [], values: [] };
    }
    return chartData[timeFrame];
  }, [chartData, timeFrame]);

  useEffect(() => {
    if (chartRef.current) {
      const existingChart = Chart.getChart(chartRef.current);
      if (existingChart) existingChart.destroy();

      const ctx = chartRef.current.getContext("2d");
      
      
      new Chart(ctx, {
        type: "bar",
        data: {
          labels: currentData.labels,
          datasets: [
            {
              label: `${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)} Orders`,
              data: currentData.values,
              borderColor: "#36a2eb",
              backgroundColor: "rgba(54, 162, 235, 0.7)",
              borderWidth: 1,
              borderRadius: 4,
              hoverBackgroundColor: "rgba(54, 162, 235, 0.9)",
              barThickness: 'flex',
              maxBarThickness: 30
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              grid: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return value.toLocaleString();
                }
              }
            }
          },
          plugins: {
            datalabels: {
              display: false,
            },
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                title: function(context) {
                  return context[0].label;
                },
                label: function(context) {
                  return `${context.raw.toLocaleString()} orders`;
                }
              }
            }
          }
        }
      });
    }
  }, [currentData, timeFrame]);
  
  const handleTimeFrameChange = (value) => {
    setTimeFrame(value);
  };

  return (
    <Card 
      title="ORDERS TREND" 
      className="dashboard-card"
      extra={
        <Select 
          defaultValue="daily" 
          style={{ width: 120 }} 
          onChange={handleTimeFrameChange}
          value={timeFrame}
        >
          <Option value="daily">Daily</Option>
          <Option value="weekly">Weekly</Option>
          <Option value="monthly">Monthly</Option>
          <Option value="yearly">Yearly</Option>
        </Select>
      }
    >
      <div style={{ height: "250px" }}>
        <canvas ref={chartRef}></canvas>
      </div>
    </Card>
  );
};

export default OrdersChart;
